import PropTypes from 'prop-types';
import {
  ChevronUp,
  ChevronDown,
  Table,
  Code,
  BarChart3,
  Lightbulb,
  Search,
  ThumbsUp,
  ThumbsDown,
  Download,
  Maximize2,
  ClipboardList
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';

const ControlButtons = ({ 
  hasSqlQuery, 
  showSql, 
  setShowSql,
  showJson, 
  setShowJson,
  hasTableResults, 
  showTable, 
  setShowTable,
  hasChartData, 
  showVisualizeData, 
  showChart, 
  setShowChart,
  hasInsights, 
  showInsights, 
  setShowInsights,
  hasInvestigations, 
  showInvestigations, 
  setShowInvestigations,
  existingFeedback,
  setFeedbackType,
  setShowFeedback,
  rawResponse,
  hasExplanation,
  onToggleExplanation
}) => {
  // Force re-render when settings change
  const [, setForceUpdate] = useState(0);
  // State for expanded table view
  const [showExpandedTable, setShowExpandedTable] = useState(false);
  
  // State for modal dragging
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  
  // State for modal resizing
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState({ width: 0, height: 0 }); // Start with zero, will be calculated
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
  
  // State for column sorting
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  
  // Add a new state variable for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Reference to the dialog panel for dragging
  const dialogRef = useRef(null);

  // Calculate optimal size based on viewport dimensions (80% of viewport)
  const calculateOptimalSize = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    return {
      width: Math.round(viewportWidth * 0.8),  // 80% of viewport width
      height: Math.round(viewportHeight * 0.8) // 80% of viewport height
    };
  };

  // Listen for settings changes and re-render
  useEffect(() => {
    const handleSettingsChange = (event) => {
      if (['showJsonResponse', 'showTableResponse', 'showSqlResponse', 
           'showInsightsResponse', 'showInvestigationsResponse', 
           'showVisualizeData'].includes(event.detail.key)) {
        // Force re-render when any of these settings change
        setForceUpdate(prev => prev + 1);
      }
    };
    
    window.addEventListener('settingsChanged', handleSettingsChange);
    
    return () => {
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, []);

  // Update size when window is resized
  useEffect(() => {
    const handleResize = () => {
      if (showExpandedTable) {
        setSize(calculateOptimalSize());
      }
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showExpandedTable]);
  
  // Reset position, size, and sorting when dialog is opened or closed
  useEffect(() => {
    if (showExpandedTable) {
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      setIsResizing(false);
      setSize(calculateOptimalSize()); // Use 80% of viewport
      setSortConfig({ key: null, direction: 'ascending' }); // Reset sorting
      setSearchTerm(''); // Reset search term when dialog is opened
    }
  }, [showExpandedTable]);
  
  // Handle mouse events for dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.dialog-header')) {
      setIsDragging(true);
      // Store initial mouse position rather than trying to compute based on existing position
      setInitialPosition({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  
  const handleMouseMove = (e) => {
    if (isDragging) {
      // Calculate position change from initial click point
      const deltaX = e.clientX - initialPosition.x;
      const deltaY = e.clientY - initialPosition.y;
      
      // Update position based on the delta from when dragging started
      setPosition({
        x: position.x + deltaX,
        y: position.y + deltaY
      });
      
      // Update initial position for next move
      setInitialPosition({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Handle resize events
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setInitialMousePos({ x: e.clientX, y: e.clientY });
    setInitialSize({ ...size });
  };
  
  const handleResizeMouseMove = (e) => {
    if (isResizing) {
      const deltaX = e.clientX - initialMousePos.x;
      const deltaY = e.clientY - initialMousePos.y;
      
      // Limit maximum size to 95% of viewport
      const maxWidth = window.innerWidth * 0.95;
      const maxHeight = window.innerHeight * 0.95;
      
      setSize({
        width: Math.min(maxWidth, Math.max(600, initialSize.width + deltaX)),  // Between 600px and 95% of viewport
        height: Math.min(maxHeight, Math.max(400, initialSize.height + deltaY)) // Between 400px and 95% of viewport
      });
    }
  };
  
  const handleResizeMouseUp = () => {
    setIsResizing(false);
  };
  
  // Add and remove global event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
    } else {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
    };
  }, [isResizing, initialMousePos, initialSize]);
  
  // Add and remove global event listeners for dragging and resizing
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, initialPosition]);
  
  // Function to download table data as CSV
  const downloadTableAsCSV = () => {
    if (!rawResponse?.results?.rows) return;
    
    const { rows, columns } = rawResponse.results;
    const rowsAreArrays = Array.isArray(rows[0]);
    
    // Get column headers
    const headers = columns || (rowsAreArrays ? [] : Object.keys(rows[0]));
    
    // Create CSV content
    let csvContent = headers.join(',') + '\n';
    
    // Add rows
    rows.forEach(row => {
      if (rowsAreArrays) {
        // If rows are arrays, join the values
        csvContent += row.join(',') + '\n';
      } else {
        // If rows are objects, get values for each column
        const rowValues = headers.map(header => {
          const value = row[header];
          // Handle values that contain commas by wrapping in quotes
          if (value === null || value === undefined) return '';
          const valueStr = String(value);
          return valueStr.includes(',') ? `"${valueStr}"` : valueStr;
        });
        csvContent += rowValues.join(',') + '\n';
      }
    });
    
    // Create download link and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'tableData.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to sort table data
  const sortData = (data, rowsAreArrays, headers) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      if (rowsAreArrays) {
        // For array rows, the sort key is the index
        const columnIndex = parseInt(sortConfig.key);
        aValue = a[columnIndex];
        bValue = b[columnIndex];
      } else {
        // For object rows, the sort key is the column name
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }
      
      // Handle numeric sorting if both values are numbers or can be parsed as numbers
      if (!isNaN(aValue) && !isNaN(bValue)) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      } else {
        // Convert to strings for string comparison
        aValue = aValue !== null && aValue !== undefined ? String(aValue) : '';
        bValue = bValue !== null && bValue !== undefined ? String(bValue) : '';
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };
  
  // Function to request a column sort
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Render the expanded table view
  const renderExpandedTableView = () => {
    if (!rawResponse?.results?.rows) return null;
    
    const { rows, columns } = rawResponse.results;
    const rowsAreArrays = Array.isArray(rows[0]);
    
    // Get column headers - either from columns property or object keys
    const headers = columns || (rowsAreArrays ? [] : Object.keys(rows[0]));
    
    // Sort the data
    const sortedData = sortData(rows, rowsAreArrays, headers);
    
    // Filter the sorted data based on search term
    const filteredData = searchTerm 
      ? sortedData.filter(row => {
          // For arrays, check each value in the row
          if (rowsAreArrays) {
            return row.some(cell => 
              cell !== null && 
              cell !== undefined && 
              String(cell).toLowerCase().includes(searchTerm.toLowerCase())
            );
          } 
          // For objects, check each property value
          else {
            return Object.values(row).some(value => 
              value !== null && 
              value !== undefined && 
              String(value).toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
        })
      : sortedData;

    // Dialog position and size style - simplified for better positioning
    const dialogStyle = {
      width: `${size.width}px`,
      height: `${size.height}px`,
      maxWidth: '80vw', // Reduced to 80% of viewport width
      maxHeight: '80vh', // Reduced to 80% of viewport height
      position: 'absolute',
      ...(isDragging || position.x !== 0 || position.y !== 0
        ? {
            top: `calc(50% + ${position.y}px)`,
            left: `calc(50% + ${position.x}px)`,
            transform: 'translate(-50%, -50%)',
            transition: isDragging || isResizing ? 'none' : 'all 0.2s ease',
            cursor: isDragging ? 'grabbing' : 'auto',
          }
        : {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          })
    };
    
    // Helper function to get sort indicator
    const getSortIndicator = (key) => {
      if (sortConfig.key !== key) return null;
      return sortConfig.direction === 'ascending' 
        ? <span className="ml-1">↑</span> 
        : <span className="ml-1">↓</span>;
    };
    
    return (
      <Dialog
        open={showExpandedTable}
        onClose={() => setShowExpandedTable(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center overflow-hidden">
          <Dialog.Panel 
            ref={dialogRef}
            className="rounded bg-white flex flex-col shadow-xl relative"
            style={dialogStyle}
            onMouseDown={handleMouseDown}
          >
            <div 
              className="dialog-header flex justify-between items-center p-4 bg-gray-50 border-b cursor-move"
              onMouseDown={handleMouseDown}
            >
              <Dialog.Title className="text-lg font-medium">
                Table Results ({filteredData.length}/{rows.length} rows)
              </Dialog.Title>
              
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search table..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-3 py-1 pr-8 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  onClick={downloadTableAsCSV}
                  className="flex items-center text-xs px-2 py-1 border rounded text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  title="Download as CSV"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowExpandedTable(false)}
                  className="flex items-center text-xs px-2 py-1 border rounded text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="overflow-auto flex-grow p-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                  <tr>
                    {headers.map((column, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => requestSort(rowsAreArrays ? index.toString() : column)}
                      >
                        {column}
                        {getSortIndicator(rowsAreArrays ? index.toString() : column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length > 0 ? (
                    filteredData.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {rowsAreArrays ? (
                          // Handle rows as arrays - use index to access values
                          row.map((cell, cellIndex) => (
                            <td
                              key={cellIndex}
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                            >
                              {cell !== null && cell !== undefined ? String(cell) : ''}
                            </td>
                          ))
                        ) : (
                          // Handle rows as objects - use column names to access values
                          headers.map((column, colIndex) => (
                            <td
                              key={colIndex}
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                            >
                              {row[column] !== null && row[column] !== undefined ? String(row[column]) : ''}
                            </td>
                          ))
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={headers.length} className="px-6 py-4 text-center text-sm text-gray-500">
                        No matching results found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Resize handle */}
            <div 
              className="absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize" 
              onMouseDown={handleResizeMouseDown}
              style={{
                background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.1) 50%)'
              }}
            />
          </Dialog.Panel>
        </div>
      </Dialog>
    );
  };

  return (
    <div className="flex flex-wrap gap-3">
      {hasSqlQuery && (
        <button
          onClick={() => {
            const newValue = !showSql;
            setShowSql(newValue);
            if (newValue) window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
          }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
        >
          {showSql ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide SQL
            </>
          ) : (
            <>
              <Code className="h-4 w-4 mr-1" />
              Show SQL
            </>
          )}
        </button>
      )}

      {localStorage.getItem('showJsonResponse') === 'true' && (
        <button
          onClick={() => {
            const newValue = !showJson;
            setShowJson(newValue);
            if (showTable) setShowTable(false);
            if (newValue) window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
          }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
        >
          {showJson ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide JSON
            </>
          ) : (
            <>
              <Code className="h-4 w-4 mr-1" />
              Show JSON
            </>
          )}
        </button>
      )}
      
      {/* --- Re-adding Explanation Button Here --- */}
      {hasExplanation && (
        <button
          onClick={onToggleExplanation}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
          title="Show AI Reasoning"
        >
          <ClipboardList className="h-5 w-5" />
          <span>Explain</span>
        </button>
      )}
      {/* --- End Explanation Button --- */}
      
      {/* Add debug logging to check why table button isn't showing */}
      {console.log('Table button conditions:', {
        hasTableResults,
        showTableResponseSetting: localStorage.getItem('showTableResponse'),
        showTableResponseBool: localStorage.getItem('showTableResponse') === 'true',
        buttonShouldShow: hasTableResults && localStorage.getItem('showTableResponse') === 'true'
      })}
      
      {hasTableResults && (
        <button
          onClick={() => {
            const newValue = !showTable;
            setShowTable(newValue);
            if (showJson) setShowJson(false);
            if (newValue) window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
          }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
        >
          {showTable ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Results
            </>
          ) : (
            <>
              <Table className="h-4 w-4 mr-1" />
              Show Results
            </>
          )}
        </button>
      )}
      
      {/* CSV Download button - only show when table is visible */}
      {showTable && hasTableResults && (
        <>
          <button
            onClick={downloadTableAsCSV}
            className="flex items-center text-xs text-gray-700 hover:text-gray-900"
            title="Download as CSV"
          >
            <Download className="h-4 w-4" />
          </button>

          {/* Expand button for larger table view */}
          <button
            onClick={() => setShowExpandedTable(true)}
            className="flex items-center text-xs text-gray-700 hover:text-gray-900"
            title="Expand table view"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </>
      )}
      
      {/* Show the Visualize Data button if visualization is enabled, but disable it if no chart data */}
      {showVisualizeData && (
        <button
          onClick={() => {
            if (hasChartData) {
              const newValue = !showChart;
              setShowChart(newValue);
              if (newValue) window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
            }
          }}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
            hasChartData
              ? 'text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border-gray-200'
              : 'text-gray-400 bg-gray-50 border-gray-200 cursor-not-allowed'
          }`}
          disabled={!hasChartData}
        >
          {showChart && hasChartData ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Visualisation
            </>
          ) : (
            <>
              <BarChart3 className="h-4 w-4 mr-1" />
              Visualise Data
            </>
          )}
        </button>
      )}
      
      {hasInsights && (
        <button
          onClick={() => {
            const newValue = !showInsights;
            setShowInsights(newValue);
            if (newValue) window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
          }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
        >
          {showInsights ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Insights
            </>
          ) : (
            <>
              <Lightbulb className="h-4 w-4 mr-1" />
              Show Insights
            </>
          )}
        </button>
      )}
      
      {hasInvestigations && (
        <button
          onClick={() => {
            const newValue = !showInvestigations;
            setShowInvestigations(newValue);
            if (newValue) window.dispatchEvent(new CustomEvent('chatSectionExpanded'));
          }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
        >
          {showInvestigations ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Investigations
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-1" />
              Show Investigations
            </>
          )}
        </button>
      )}

      {/* Feedback buttons - separated with visual divider */}
      <div className="flex items-center gap-1 pl-2 ml-auto border-l border-gray-200">
        <button
          onClick={() => {
            setFeedbackType('positive');
            setShowFeedback(true);
          }}
          className={`p-2 rounded-lg transition-colors ${
            existingFeedback?.type === 'positive'
              ? 'text-green-600 bg-green-50'
              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
          }`}
          title="Helpful"
        >
          <ThumbsUp className="h-5 w-5" />
        </button>

        <button
          onClick={() => {
            setFeedbackType('negative');
            setShowFeedback(true);
          }}
          className={`p-2 rounded-lg transition-colors ${
            existingFeedback?.type === 'negative'
              ? 'text-red-600 bg-red-50'
              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
          }`}
          title="Not helpful"
        >
          <ThumbsDown className="h-5 w-5" />
        </button>
      </div>
      
      {/* Render the expanded table dialog */}
      {renderExpandedTableView()}

      {/* --- End Explanation Button --- */}
    </div>
  );
};

ControlButtons.propTypes = {
  hasSqlQuery: PropTypes.bool.isRequired,
  showSql: PropTypes.bool.isRequired,
  setShowSql: PropTypes.func.isRequired,
  showJson: PropTypes.bool.isRequired,
  setShowJson: PropTypes.func.isRequired,
  hasTableResults: PropTypes.bool.isRequired,
  showTable: PropTypes.bool.isRequired,
  setShowTable: PropTypes.func.isRequired,
  hasChartData: PropTypes.bool.isRequired,
  showVisualizeData: PropTypes.bool.isRequired,
  showChart: PropTypes.bool.isRequired,
  setShowChart: PropTypes.func.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  showInsights: PropTypes.bool.isRequired,
  setShowInsights: PropTypes.func.isRequired,
  hasInvestigations: PropTypes.bool.isRequired,
  showInvestigations: PropTypes.bool.isRequired,
  setShowInvestigations: PropTypes.func.isRequired,
  existingFeedback: PropTypes.object,
  setFeedbackType: PropTypes.func.isRequired,
  setShowFeedback: PropTypes.func.isRequired,
  rawResponse: PropTypes.object,
  hasExplanation: PropTypes.bool.isRequired,
  onToggleExplanation: PropTypes.func.isRequired
};

export default ControlButtons; 